import { useState, useEffect, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { FiX, FiSmartphone, FiRefreshCw, FiClock } from 'react-icons/fi';
import api from '../lib/api';

const MobileAuthModal = ({ isOpen, onClose }) => {
  const [qrData, setQrData] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const generateToken = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/mobile-auth/generate-token');
      setQrData(response.data.qrData);
      setExpiresAt(new Date(response.data.expiresAt));
    } catch (err) {
      console.error('Failed to generate mobile auth token:', err);
      setError(err.response?.data?.error || 'Failed to generate QR code');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      generateToken();
    } else {
      setQrData(null);
      setExpiresAt(null);
      setTimeLeft(0);
      setError(null);
    }
  }, [isOpen, generateToken]);

  useEffect(() => {
    if (!expiresAt) return;

    const updateTimer = () => {
      const now = new Date();
      const diff = Math.max(0, Math.floor((expiresAt - now) / 1000));
      setTimeLeft(diff);

      if (diff === 0) {
        setQrData(null);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [expiresAt]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FiSmartphone className="w-6 h-6 text-white" />
            <h2 className="text-xl font-semibold text-white">Mobile App Authentication</h2>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors"
          >
            <FiX className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              <p className="mt-4 text-gray-600">Generating QR Code...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-red-500 mb-4">
                <p className="font-medium">Error</p>
                <p className="text-sm">{error}</p>
              </div>
              <button
                onClick={generateToken}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <FiRefreshCw className="w-4 h-4" />
                Try Again
              </button>
            </div>
          ) : qrData && timeLeft > 0 ? (
            <div className="flex flex-col items-center">
              <div className="bg-white p-4 rounded-xl shadow-inner border-2 border-gray-100">
                <QRCodeSVG
                  value={qrData}
                  size={220}
                  level="H"
                  includeMargin={true}
                  bgColor="#ffffff"
                  fgColor="#1e293b"
                />
              </div>

              <div className="mt-6 text-center">
                <div className="flex items-center justify-center gap-2 text-gray-600 mb-2">
                  <FiClock className="w-4 h-4" />
                  <span className="text-sm">Expires in</span>
                  <span className={`font-mono font-bold ${timeLeft <= 30 ? 'text-red-500' : 'text-primary-600'}`}>
                    {formatTime(timeLeft)}
                  </span>
                </div>

                <button
                  onClick={generateToken}
                  className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
                >
                  <FiRefreshCw className="w-4 h-4" />
                  Generate New Code
                </button>
              </div>

              <div className="mt-6 bg-gray-50 rounded-lg p-4 text-center">
                <h3 className="font-medium text-gray-900 mb-2">How to connect:</h3>
                <ol className="text-sm text-gray-600 text-left space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="bg-primary-100 text-primary-700 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                    <span>Open the CIM DMS mobile app</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-primary-100 text-primary-700 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                    <span>Tap "Scan QR Code" on the login screen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-primary-100 text-primary-700 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                    <span>Point your camera at this QR code</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-primary-100 text-primary-700 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-bold">4</span>
                    <span>You'll be automatically logged in</span>
                  </li>
                </ol>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-600 mb-4">QR code has expired</p>
              <button
                onClick={generateToken}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <FiRefreshCw className="w-4 h-4" />
                Generate New Code
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileAuthModal;
